import { ArrowLeft, Hammer, Heart, Sparkles, Flower, Cookie, Scissors } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function HandsOnCreatingGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-orange-700 hover:text-orange-800 hover:bg-orange-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-100 rounded-full">
              <Hammer className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Hands-On Creating</h1>
              <p className="text-orange-600">Week 11, Day 5 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-orange max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Today, start a tactile project - something you can touch, mold, and shape with your hands. 
              Lose yourself in the doing. The satisfaction of creating something tangible is deeply fulfilling.
            </p>

            <div className="bg-orange-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-orange-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Tactile Creation Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Provides sense of accomplishment</li>
                <li>• Reduces anxiety and stress</li>
                <li>• Improves focus and concentration</li>
                <li>• Creates flow state (time flies)</li>
                <li>• Produces tangible results you can see and use</li>
                <li>• Engages different brain areas than screen work</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Project Ideas</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Flower className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">Garden & Nature</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Repot a plant</li>
                  <li>• Start seeds indoors</li>
                  <li>• Create a terrarium</li>
                  <li>• Arrange fresh flowers</li>
                  <li>• Make a wreath</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">Kitchen Creations</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Bake bread from scratch</li>
                  <li>• Decorate cookies or cupcakes</li>
                  <li>• Make homemade pasta</li>
                  <li>• Create a charcuterie board</li>
                  <li>• Try a new elaborate recipe</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Scissors className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">Fiber Arts</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Knit or crochet</li>
                  <li>• Embroider something</li>
                  <li>• Sew a simple project</li>
                  <li>• Macramé</li>
                  <li>• Weaving</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Hammer className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">DIY Projects</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Build a simple shelf</li>
                  <li>• Refinish furniture</li>
                  <li>• Create a photo frame</li>
                  <li>• Make candles or soap</li>
                  <li>• Assemble a puzzle</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-orange-800 mb-4">Getting Into Flow</h2>
              <p className="text-gray-700 mb-4">
                Flow state happens when you're fully absorbed in an activity. To encourage it:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Choose something slightly challenging but not frustrating</li>
                <li>• Remove distractions (phone on silent, no TV)</li>
                <li>• Set aside enough time (at least 30-60 minutes)</li>
                <li>• Put on background music if it helps you focus</li>
                <li>• Give yourself permission to make mistakes</li>
              </ul>
            </div>

            <div className="bg-emerald-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Starting Points
              </h2>
              <p className="text-gray-700 mb-4">
                Not sure where to begin? Try these beginner-friendly options:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Easiest:</strong> Color in an adult coloring book, assemble a puzzle</li>
                <li>• <strong>Quick win:</strong> Arrange flowers, make a simple recipe</li>
                <li>• <strong>Learn something new:</strong> Follow a YouTube tutorial for a craft</li>
                <li>• <strong>Nostalgic:</strong> Return to a hobby you loved as a kid</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
